import type { Character } from '@guildrise/shared';
import { useId, useState } from 'react';
import './deck-editor.css';

const DECK_SIZE = 9;
const SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const RARITIES: Readonly<Record<Character['rarity'], string>> = {
  rare: 'Редкая',
  epic: 'Эпическая',
  legendary: 'Легендарная',
  mythic: 'Мифическая',
};

export interface DeckEditorProps {
  readonly catalog: readonly Character[];
  readonly owned: readonly string[];
  readonly selected: readonly string[];
  readonly onChange: (ids: string[]) => void;
}

export function filterOwnedCharacters(
  catalog: readonly Character[],
  owned: readonly string[],
  filters: { readonly search: string; readonly universe: string; readonly rarity: string },
): Character[] {
  const ownedIds = new Set(owned);
  const query = filters.search.trim().toLocaleLowerCase('ru');
  return catalog.filter(
    (character) =>
      ownedIds.has(character.character_id) &&
      character.name.toLocaleLowerCase('ru').includes(query) &&
      (!filters.universe || character.universe_id === filters.universe) &&
      (!filters.rarity || character.rarity === filters.rarity),
  );
}

export function toggleDeckCharacter(
  selected: readonly string[],
  id: string,
  owned: readonly string[],
): string[] {
  if (selected.includes(id)) return selected.filter((current) => current !== id);
  if (selected.length >= DECK_SIZE || !owned.includes(id)) return [...selected];
  return [...selected, id];
}

function DeckPortrait({ character }: { readonly character: Character }) {
  const [failedArt, setFailedArt] = useState<string | null>(null);
  const hasArt = character.art.length > 0 && failedArt !== character.art;
  return (
    <span className="deck-editor__portrait" aria-hidden="true">
      {hasArt ? (
        <img
          className="deck-editor__art"
          src={`/art/cards/${character.art}-360.webp`}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailedArt(character.art)}
        />
      ) : (
        <span className="deck-editor__art-fallback">Арт недоступен</span>
      )}
      <img
        className="deck-editor__frame"
        src={`/art/rarity-frames/${character.rarity}.svg`}
        alt=""
        loading="lazy"
        draggable={false}
      />
    </span>
  );
}

/** Controlled content for the parent's ArenaChoice; selection is never persisted here. */
export function DeckEditor({ catalog, owned, selected, onChange }: DeckEditorProps) {
  const [search, setSearch] = useState('');
  const [universe, setUniverse] = useState('');
  const [rarity, setRarity] = useState('');
  const id = useId();
  const characters = new Map(catalog.map((character) => [character.character_id, character]));
  const ownedIds = new Set(owned);
  const selectedIds = new Set(selected);
  const ownedCharacters = catalog.filter((character) => ownedIds.has(character.character_id));
  const universes = [...new Set(ownedCharacters.map((character) => character.universe_id))].sort(
    (a, b) => a.localeCompare(b, 'ru'),
  );
  const visible = filterOwnedCharacters(catalog, owned, { search, universe, rarity });
  const full = selected.length >= DECK_SIZE;

  function toggle(characterId: string) {
    onChange(toggleDeckCharacter(selected, characterId, owned));
  }

  return (
    <section className="deck-editor" aria-label="Состав колоды">
      <div className="deck-editor__summary">
        <strong>Твоя колода</strong>
        <span className="tabular" aria-live="polite">
          {selected.length} / {DECK_SIZE}
        </span>
      </div>
      <ol className="deck-editor__slots" aria-label="Девять мест в колоде">
        {SLOTS.map((number) => {
          const characterId = selected[number - 1];
          const character = characterId === undefined ? undefined : characters.get(characterId);
          return (
            <li key={number}>
              {characterId !== undefined ? (
                <button
                  type="button"
                  className="deck-editor__slot"
                  aria-label={`Убрать ${character?.name ?? characterId} из колоды`}
                  onClick={() => toggle(characterId)}
                >
                  {character && <DeckPortrait character={character} />}
                  <span className="deck-editor__slot-name">
                    {character?.name ?? 'Карта недоступна'}
                  </span>
                  <span className="deck-editor__remove" aria-hidden="true">
                    ×
                  </span>
                </button>
              ) : (
                <div className="deck-editor__empty">
                  <span className="tabular">{number}</span>
                  <small>Выбери ниже</small>
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <p className="deck-editor__hint" id={`${id}-capacity`} aria-live="polite">
        {full
          ? 'Все 9 мест заняты. Убери карту из колоды, чтобы добавить другую.'
          : 'Нажми на персонажа в коллекции, чтобы добавить. На карту в колоде — чтобы убрать.'}
      </p>
      <div className="deck-editor__collection">
        <h3>Твоя коллекция</h3>
        <div className="deck-editor__filters">
          <label className="deck-editor__search">
            Поиск персонажа
            <input
              type="search"
              value={search}
              placeholder="Имя персонажа"
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <label>
            Вселенная
            <select value={universe} onChange={(event) => setUniverse(event.target.value)}>
              <option value="">Все вселенные</option>
              {universes.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            Редкость
            <select value={rarity} onChange={(event) => setRarity(event.target.value)}>
              <option value="">Любая редкость</option>
              {Object.entries(RARITIES).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="deck-editor__hint">Редкость меняет оформление, а не силу карты.</p>
        <p className="deck-editor__hint" role="status">
          Найдено: {visible.length}
        </p>
        {visible.length > 0 ? (
          <ul className="deck-editor__cards" aria-label="Персонажи из твоей коллекции">
            {visible.map((character) => {
              const active = selectedIds.has(character.character_id);
              return (
                <li key={character.character_id}>
                  <button
                    type="button"
                    className="deck-editor__card"
                    aria-pressed={active}
                    aria-label={`${active ? 'Убрать' : 'Добавить'} ${character.name}${active ? ' из колоды' : ' в колоду'}`}
                    aria-describedby={full && !active ? `${id}-capacity` : undefined}
                    disabled={full && !active}
                    onClick={() => toggle(character.character_id)}
                  >
                    <DeckPortrait character={character} />
                    <strong>{character.name}</strong>
                    <small>{character.universe_id}</small>
                    <span className="deck-editor__card-state">
                      {active ? '✓ В колоде' : full ? 'Нет места' : '+ Добавить'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="deck-editor__no-results">
            <p>
              {ownedCharacters.length === 0
                ? 'В коллекции пока нет персонажей. Для колоды нужны 9 разных карт.'
                : 'Персонажи не найдены. Попробуй другое имя или сбрось фильтры.'}
            </p>
            {(search || universe || rarity) && (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setSearch('');
                  setUniverse('');
                  setRarity('');
                }}
              >
                Сбросить фильтры
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
